import { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { SectionHero } from '../components/sections/SectionHero';
import { SEO } from '../components/SEO';
import { PAGE_SEO } from '../config/seoCopy';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { GdprConsent } from '../components/GdprConsent';
import axios from 'axios';
import { trackWhatsAppLead } from '../lib/trackLead';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactePage = () => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    privacy: false,
    commercial: false,
    honeypot: '' // Anti-spam
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check honeypot (anti-spam)
    if (formData.honeypot) {
      return;
    }

    if (!formData.privacy) {
      setStatus({ type: 'error', message: t('contact.error') });
      return;
    }

    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        subject: formData.subject,
        message: formData.message,
        language: language,
        marketing_consent: formData.commercial
      });

      setStatus({ type: 'success', message: t('contact.gracies') });
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
        privacy: false,
        commercial: false,
        honeypot: ''
      });
    } catch (error) {
      console.error('Contact form error:', error);
      setStatus({ type: 'error', message: t('contact.error') });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main data-testid="contacte-page">
      <SEO title={PAGE_SEO.contacte.title} description={PAGE_SEO.contacte.description} />
      <SectionHero
        image="https://customer-assets.emergentagent.com/job_aravell-staging/artifacts/x2egcw5u_ARAVELL_HERO_CONTACTE_CASA_CLUB_1920x700.jpeg"
        title={t('contact.title')}
        subtitle={t('contact.subtitle')}
      />

      <section className="section-spacing bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            {/* Contact Form */}
            <motion.div 
              className="lg:col-span-7"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                {/* Honeypot field - hidden */}
                <div className="hidden" aria-hidden="true">
                  <input
                    type="text"
                    name="honeypot"
                    value={formData.honeypot}
                    onChange={handleChange}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-xs font-semibold tracking-wider uppercase text-[#666666]">
                      {t('contact.nom')} *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="form-input"
                      data-testid="contact-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-xs font-semibold tracking-wider uppercase text-[#666666]">
                      {t('contact.email')} *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="form-input"
                      data-testid="contact-email"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-xs font-semibold tracking-wider uppercase text-[#666666]">
                      {t('contact.telefon')}
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      className="form-input"
                      data-testid="contact-phone"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="subject" className="text-xs font-semibold tracking-wider uppercase text-[#666666]">
                      {t('contact.assumpte')} *
                    </Label>
                    <Input
                      id="subject"
                      name="subject"
                      type="text"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="form-input"
                      data-testid="contact-subject"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-xs font-semibold tracking-wider uppercase text-[#666666]">
                    {t('contact.missatge')} *
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="form-input resize-none"
                    data-testid="contact-message"
                  />
                </div>

                <GdprConsent
                  privacy={formData.privacy}
                  onPrivacyChange={(checked) => setFormData(prev => ({ ...prev, privacy: checked }))}
                  marketing={formData.commercial}
                  onMarketingChange={(checked) => setFormData(prev => ({ ...prev, commercial: checked }))}
                  showMarketing
                  idPrefix="contact"
                />

                {/* Status message */}
                {status.message && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex items-center gap-2 p-4 ${
                      status.type === 'success' 
                        ? 'bg-green-50 text-green-700 border border-green-200' 
                        : 'bg-red-50 text-red-700 border border-red-200'
                    }`}
                    data-testid="contact-status"
                  >
                    {status.type === 'success' ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : (
                      <AlertCircle className="w-5 h-5" />
                    )}
                    {status.message}
                  </motion.div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full sm:w-auto bg-[#1A4D2E] text-white px-8 py-4 text-xs font-semibold tracking-[0.15em] uppercase hover:bg-[#153d24] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="contact-submit"
                >
                  {isSubmitting ? (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ repeat: Infinity, duration: 1 }}
                        className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full"
                      />
                      {t('contact.enviament')}
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      {t('contact.enviar')}
                    </>
                  )}
                </button>
              </form>
            </motion.div>

            {/* Contact Info & Map */}
            <motion.div 
              className="lg:col-span-5 space-y-8"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              {/* Contact Info */}
              <div className="bg-[#F9F9F7] p-6 space-y-6">
                <h3 className="font-display text-xl text-[#1A1A1A] font-medium">
                  UGOLF ARAVELL Andorra
                </h3>
                
                <div className="space-y-4">
                  <a 
                    href="tel:+34661194023"
                    className="flex items-center gap-3 text-[#666666] hover:text-[#1A4D2E] transition-colors duration-200"
                    data-testid="contact-info-phone"
                  >
                    <Phone className="w-5 h-5 text-[#1A4D2E]" />
                    <span>+34 661 19 40 23</span>
                  </a>
                  
                  <a 
                    href={`https://wa.me/34676047987`}
                    onClick={() => trackWhatsAppLead('contacte')}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-[#666666] hover:text-[#25D366] transition-colors duration-200"
                    data-testid="contact-info-whatsapp"
                  >
                    <svg className="w-5 h-5 text-[#25D366]" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    <span>+34 676 047 987</span>
                  </a>
                  
                  <a 
                    href="mailto:secretaria@aravellgolfclub.com"
                    className="flex items-center gap-3 text-[#666666] hover:text-[#1A4D2E] transition-colors duration-200"
                    data-testid="contact-info-email"
                  >
                    <Mail className="w-5 h-5 text-[#1A4D2E]" />
                    <span>secretaria@aravellgolfclub.com</span>
                  </a>
                  
                  <a 
                    href="https://maps.google.com/?q=Avinguda+del+Cadí+6+25712+Aravell+Lleida"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-3 text-[#666666] hover:text-[#1A4D2E] transition-colors duration-200"
                    data-testid="contact-info-address"
                  >
                    <MapPin className="w-5 h-5 text-[#1A4D2E] flex-shrink-0 mt-0.5" />
                    <span>Avinguda del Cadí, 6<br />25712 Aravell, Lleida</span>
                  </a>
                </div>
              </div>

              {/* Map */}
              <div className="aspect-[4/3] bg-[#F0F2EF]" data-testid="contact-map">
                <iframe
                  src="https://maps.google.com/maps?q=42.3604961,1.4052768&t=&z=15&ie=UTF8&iwloc=&output=embed"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="UGOLF ARAVELL Andorra Location"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ContactePage;
